import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, sample } from "effector";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { IUpsellContract } from "../model";
import { routePathNameChanged } from "@features/app/model";
import { interval, reset } from "patronum";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IUpsellUIContract } from ".";

const TARGET_IDS = new Set(["600_prompts", "600_prompts_sale"]);

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
const modalOpened = createEvent();
const modalClosed = createEvent();
const skipAndContinueButtonClicked = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();

const $isModalOpen = createStore(false)
    .on(modalOpened, () => true)
    .on(modalClosed, () => false);
const $upsellIds = createStore<string[]>([]);
// Whether the student's chain offers the pack. A chain without it (drama /
// film) still gets the pack checked — the cancellation offer grants it for
// free — but shows it only once owned, never as an offer.
const $isOffered = createStore(false);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 10000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const $personalBoostPackStatus = createStore<UpsellStatus | null>(null);
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);

const contract = new ModelContract(
    { $isModalOpen, $personalBoostPackStatus, $upsellIds, $isBuyButtonDisabled },
    { buyButtonClicked, modalOpened, modalClosed, skipAndContinueButtonClicked },
    {},
    {}
);

export type IPersonalBoostPackContract = typeof contract;

export class PersonalBoostPackModule extends ModuleDescription<IPersonalBoostPackContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public subscriptionsModule: ISubscriptionContract,
        public routesModule: IAppRouteUnitsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // The chain holds upsell ids for every provider (truegate plans are
        // resolved on the server now), so the check needs no catalog.
        sample({
            source: {
                upsells: this.upsellModule.stores.$upsells,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => s.upsells.length > 0,
            fn: (s) => {
                const offered = s.upsells.filter((u) => TARGET_IDS.has(u.upsellId)).map((u) => u.upsellId);
                const result = offered.length > 0 ? [...offered] : ["600_prompts"];
                if (!result.includes("bundle")) {
                    result.push("bundle");
                }
                // The Complete Bundle grants this pack too.
                if (!result.includes("complete_bundle")) {
                    result.push("complete_bundle");
                }
                return result;
            },
            target: $upsellIds
        });

        sample({
            source: this.upsellModule.stores.$upsells,
            fn: (upsells) => upsells.some((u) => TARGET_IDS.has(u.upsellId)),
            target: $isOffered
        });

        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === "600_prompts",
            target: modalOpened
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        sample({
            clock: skipAndContinueButtonClicked,
            target: this.upsellUiModule.events.skipButtonClicked
        });

        sample({
            source: {
                upsellIds: $upsellIds,
                upsellPath: this.routesModule.stores.upsell.$path,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isLibraryRoute: this.routesModule.stores.library.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isChallengesRoute: this.routesModule.stores.challenges.$isActive,
                promptLibraryQuery: this.routesModule.stores.promptsLibrary.$query,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                (s.isHomeRoute ||
                    s.isProfileRoute ||
                    // Desktop right rail shows the packs on these routes too.
                    s.isLibraryRoute ||
                    s.isAiToolsRoute ||
                    s.isChallengesRoute ||
                    s.promptLibraryQuery.pack === "personal_boost" ||
                    (s.upsellPath !== null &&
                        (s.upsellPath.includes("600_prompts") || s.upsellPath.includes("bundle")))) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                upsellIds: $upsellIds,
                isOffered: $isOffered,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                promptLibraryQuery: this.routesModule.stores.promptsLibrary.$query,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict
            },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem),
            fn: (s, { result }) => {
                const statuses = s.upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));

                if (statuses.includes("Success")) return "Success";
                // Email links can open a standalone offer outside the frozen
                // chain. Keep it hidden on home, but allow this screen to buy it
                // — and let the pack's library page see the purchase in progress:
                // the charge answers "processing" first, the buy redirects there,
                // and a null status would keep its loader up until a reload.
                const standaloneOffer =
                    (s.isUpsellRoute && TARGET_IDS.has(s.currentUpsellId ?? "")) ||
                    s.promptLibraryQuery.pack === "personal_boost";
                if (!s.isOffered && !standaloneOffer) return null;
                if (statuses.includes("Processing")) return "Processing";
                // A just-bought pack in 3ds_verify reads as "Fail" until its bank
                // dialog is done; it is in progress — keep polling until the
                // webhook confirms instead of showing it as unpurchased.
                if (s.upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";

                return "Fail";
            },
            target: $personalBoostPackStatus
        });

        // The pack's library page is not for a student whose chain never sells
        // the pack (drama / film) unless they own it: send them home. A chain
        // that offers it keeps the page as the preview with its Unlock button —
        // that is where the home / profile banners lead before the purchase.
        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                upsellIds: $upsellIds,
                isOffered: $isOffered,
                query: this.routesModule.stores.promptsLibrary.$query
            },
            filter: (s, { params, result }) =>
                s.query.pack === "personal_boost" &&
                !s.isOffered &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)) &&
                !s.upsellIds.some((id) => ["Success", "Processing"].includes(checkUpsellPurchased(result, id))),
            fn: () => "/home",
            target: this.routerModule.events.routerReplace
        });

        sample({
            source: {
                personalBoostPackStatus: $personalBoostPackStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) =>
                (s.personalBoostPackStatus === "Success" || s.personalBoostPackStatus === "Processing") &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("600_prompts") &&
                !s.firstLoginFlag,
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                personalBoostPackStatus: $personalBoostPackStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path
            },
            filter: (s) =>
                s.personalBoostPackStatus === "Fail" &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("600_prompts"),
            target: buyButtonReset
        });

        sample({
            clock: $personalBoostPackStatus,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s, p) => !s.isUpsellRoute && p === "Processing",
            target: pollingStarted
        });

        sample({
            clock: $personalBoostPackStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: pollingStopped
        });

        // Clear the in-flight flag for this module's own request even when the id
        // list changed while it was running (the Complete Bundle A/B adds and
        // removes 600_prompts): matching on the request's own params rather than
        // on the current list keeps a superseded response from blocking all
        // later checks.
        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            filter: ({ params }) => params.some((id) => TARGET_IDS.has(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        // $isCheckPending is reset on navigation too: an in-flight check whose
        // id list changed meanwhile (the Complete Bundle A/B swaps 600_prompts
        // out of the sequence) never matches the `.finally` filter below, so
        // without this the flag stays `true` forever and no further check ever
        // starts — leaving the status unresolved and the buy button disabled.
        reset({
            clock: routePathNameChanged,
            target: [$isModalOpen, $personalBoostPackStatus, $isCheckRequested, $isCheckPending]
        });
    }
}
